import {
  forwardRef,
  FunctionComponent,
  MutableRefObject,
  PropsWithoutRef,
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
  useLayoutEffect
} from 'react';

import { ForwardProps } from '../../types';
import { useAfterInitialEffect, useConsolidatedRef, useItemIntersection } from '../../hooks';
import Progress from '../Progress';
import EmptyState from '../EmptyState';
import Popover from '../Popover';

import { MenuListProps, ParentMenuItemProps } from './Menu.types';
import MenuContext from './Menu.context';
import MenuItem from './MenuItem';
import { StyledLoadingItem, StyledMenuList, StyledFlyoutMenuListContainer } from './Menu.styles';
import { resizeRootEl } from './NavItemsList';
import helpers from './helpers';
import MenuGroup from './MenuGroup';

const FlyoutMenuList: FunctionComponent<MenuListProps & ForwardProps> = forwardRef(
  ({ items, parent }: PropsWithoutRef<MenuListProps>, ref: MenuListProps['ref']) => {
    const selfRef = useConsolidatedRef(ref);
    const ulRef = useRef<HTMLUListElement>(null);
    const menuListWrapperRef: MutableRefObject<HTMLElement | null> = useRef(null);
    const returnFocusRef: MutableRefObject<HTMLButtonElement | null> = useRef(null);
    const [expandedItem, setExpandedItem] = useState<ParentMenuItemProps>();
    const {
      componentId,
      loadMore,
      loading,
      scrollAt,
      emptyText,
      onItemExpand,
      pushFlyoutId,
      flyOutActiveIdStack
    } = useContext(MenuContext);

    useItemIntersection(
      ulRef,
      items.length - 1,
      () => {
        loadMore?.(parent?.item?.id);
      },
      ':scope > li'
    );

    useAfterInitialEffect(() => {
      if (expandedItem) {
        setExpandedItem(items.find(item => item.id === expandedItem.id) as ParentMenuItemProps); // FIXME
      }
    }, [expandedItem, items]);

    useEffect(() => {
      const hasExpanded = items.some(item => {
        return flyOutActiveIdStack.includes(item.id);
      });

      if (!hasExpanded) {
        returnFocusRef.current = null;
        setExpandedItem(undefined);
      }
    }, [flyOutActiveIdStack]);

    const listContent = useMemo(() => {
      if (items.length) {
        return items.map(item => {
          return helpers.isItem(item) ? (
            <MenuItem
              {...item}
              key={item.id}
              aria-haspopup={!!item.items}
              aria-expanded={item.id === expandedItem?.id}
              onExpand={(id, e) => {
                if (item.items) {
                  returnFocusRef.current = e.currentTarget as HTMLButtonElement;
                  setExpandedItem(item as ParentMenuItemProps); // FIXME
                  item?.onExpand?.(id, e);
                  onItemExpand?.(id, e);
                  pushFlyoutId(item.id || componentId);
                } else {
                  returnFocusRef.current = null;
                  setExpandedItem(undefined);
                }
              }}
            />
          ) : (
            <MenuGroup {...item} key={item.id} />
          );
        });
      }

      if (!loading) {
        return <EmptyState message={emptyText} forwardedAs='li' />;
      }

      return null;
    }, [items, loading, emptyText, expandedItem]);

    const list = (
      <StyledMenuList ref={ulRef}>
        {listContent}
        {loading && !expandedItem && (
          <StyledLoadingItem>
            <Progress placement='local' />
          </StyledLoadingItem>
        )}
      </StyledMenuList>
    );

    useLayoutEffect(() => {
      setExpandedItem(undefined);
      if (selfRef.current) {
        menuListWrapperRef.current = selfRef.current.parentElement;
        resizeRootEl(selfRef.current, selfRef.current, items.length, scrollAt);
      }
    }, [items]);

    const handleMenuListScroll = useCallback(() => {
      if (expandedItem) {
        setExpandedItem(undefined);
      }
    }, [expandedItem]);

    return (
      <>
        <StyledFlyoutMenuListContainer
          ref={selfRef}
          onScroll={handleMenuListScroll}
          data-flyout-menu-parent-id={componentId}
          data-flyout-menu-id={parent?.item.id || componentId}
        >
          {list}
        </StyledFlyoutMenuListContainer>

        {expandedItem && selfRef.current && (
          <Popover target={returnFocusRef.current} show strategy='fixed' placement='right-start'>
            <FlyoutMenuList
              items={expandedItem.items}
              parent={{
                el: selfRef.current,
                item: expandedItem,
                siblingItems: items,
                setExpandedItem,
                returnFocusRef
              }}
            />
          </Popover>
        )}
      </>
    );
  }
);

export default FlyoutMenuList;
